import { IBodyScanData, ITargetInfo, IWeightLog } from 'fitbeat.models';
import uuidv4 from 'uuid/v4';
import { BodyScanSerializer } from '../../serialization/bodyscan';
import { IBodyScansRepository } from '../interfaces/ibodyscans-repository';

export class DbBodyScansRepository implements IBodyScansRepository {
    // format ex "2019-08-22 16:36:04" so the first 10 chars are iso format
    public static convertStringToDate(testTime: string): Date {
        if (testTime == null || testTime.length < 11) {
            return new Date(0);
        }
        try {
            const timeIsoDateOnly = testTime.slice(0, 10);
            const date = new Date(timeIsoDateOnly);
            return date;
        } catch (error) {
            return new Date(0);
        }

    }

    constructor(private bodyScanDb: any) { }

    public async add(body: IBodyScanData): Promise<any> {
        const doc: any = body;
        doc._id = body.id;
        await this.bodyScanDb.put(doc);
    }

    public async get(email: string): Promise<IBodyScanData[] | undefined> {
        try {
            const query = {
                selector: {
                    userEmail: email,
                    testTime: { $gt: null },
                },
                use_index: [
                    '_design/bodyScanEmailTimeIndex',
                    'bodyScanEmailTimeIndex'],
                sort: [{ testTime: 'asc' }],
            };

            const results = await this.bodyScanDb.find(query);
            const bodyScans: IBodyScanData[] = results.docs.map(BodyScanSerializer.buildAnalyticData);
            return bodyScans;
        } catch (err) {
            if (err.status === 404) {
                return undefined;
            } else {
                throw err;
            }
        }
    }

    public async getByUid(uid: string): Promise<IBodyScanData[] | undefined> {
        try {
            const query = {
                limit: Number.MAX_SAFE_INTEGER,
                selector: {
                    uid,
                    testTime: { $gt: null },
                },
                use_index: [
                    '_design/bodyScanUidTimeIndex',
                    'bodyScanUidTimeIndex'],
                sort: [{ testTime: 'asc' }],
            };

            const results = await this.bodyScanDb.find(query);
            const bodyScans: IBodyScanData[] = results.docs.map(BodyScanSerializer.buildAnalyticData);
            return bodyScans;
        } catch (err) {
            if (err.status === 404) {
                return undefined;
            } else {
                throw err;
            }
        }
    }

    public async update(scan: IBodyScanData): Promise<IBodyScanData> {
        const dbScan = await this.bodyScanDb.get(scan.id);
        const updateDoc = {
            ...dbScan,
            _id: scan.id,
            invalid: scan.invalid,
        };
        await this.bodyScanDb.put(updateDoc);
        return updateDoc;
    }

    public async getForUsers(emails: string[], from: Date, to: Date): Promise<IBodyScanData[]> {
        const results = await this.bodyScanDb.find({
            selector: {
                userEmail: {
                    $in: emails,
                },
                testTime: {
                    $gt: from,
                    $lt: to,
                },
            },
            use_index: [
                '_design/bodyScanEmailTimeIndex',
                'bodyScanEmailTimeIndex'],
            sort: [{ testTime: 'desc' }],
        });

        return results.docs.map(BodyScanSerializer.buildAnalyticData);
    }

    public async addBodyScaleScan(scan: any): Promise<IBodyScanData> {
        const doc: any = scan;
        doc._id = uuidv4();
        await this.bodyScanDb.put(doc);
        return BodyScanSerializer.buildAnalyticData(doc);
    }

    public async getLastBodyScan(uid: string): Promise<IBodyScanData> {
        const query = {
            limit: 1,
            selector: {
                uid,
                testTime: process.env.TESTS ? { $gt: new Date(0).toISOString() } : undefined,
            },
             use_index: [
                '_design/bodyScanUidTimeIndex',
                'bodyScanUidTimeIndex'],
            sort: [{ testTime: 'desc' }],
        };
        const results = await this.bodyScanDb.find(query);
        return results.docs.map(BodyScanSerializer.buildAnalyticData)[0];
    }

    public async getByUidAndDateRange(
        uid: string, from: Date, to: Date, limit: number = Number.MAX_SAFE_INTEGER):
        Promise<IBodyScanData[]> {
        const query = {
            limit,
            selector: {
                uid,
                testTime: {
                    $gt: from,
                    $lt: to,
                },
            },
            use_index: [
                '_design/bodyScanUidTimeIndex',
                'bodyScanUidTimeIndex'],
            sort: [{ testTime: 'desc' }],
        };

        const results = await this.bodyScanDb.find(query);
        return results.docs.map(BodyScanSerializer.buildAnalyticData);
    }

    public async addManualWeightLog(weightLog: IWeightLog): Promise<IBodyScanData> {
        const doc: any = weightLog;
        doc._id = uuidv4();
        await this.bodyScanDb.put(doc);
        return BodyScanSerializer.buildAnalyticData(doc);
    }

    public async updateScanWithTargetAndMilestone(
        id: string, targetInfo: ITargetInfo | undefined, milestoneInfo: ITargetInfo | undefined)
        : Promise<IBodyScanData> {
        const scan = await this.bodyScanDb.get(id);
        const updateDoc = {
            ...scan,
            _id: id,
            target: targetInfo,
            milestone: milestoneInfo,
        };
        await this.bodyScanDb.put(updateDoc);
        return updateDoc;
    }
}
